/**
 * SFX: **Native** uses expo-audio. **Web (incl. macOS Safari)** uses Web Audio API oscillators — Safari often blocks
 * remote `<audio>` / async loads outside a strict gesture chain; synth clips play reliably after `AudioContext.resume()`.
 * Call `warmFromUserGesture()` on first pointer/tap (see `app/_layout.tsx` + `SoundPressable`).
 */

import {
  createAudioPlayer,
  setAudioModeAsync,
  type AudioPlayer,
} from 'expo-audio';
import { Platform } from 'react-native';
import { getAssetUrl, ASSET_PATHS } from '../../../constants/assets';
import { logger } from '../../helpers/logger.helper';
import {
  getMenuOpenSwishBundledUriForWeb,
  getNativeMenuOpenSwishSource,
} from '../helpers/menuOpenSwishBundledSound.helper';
import {
  getLevelUpBundledUriForWeb,
  getNativeLevelUpSource,
  getNativeRankUpSource,
  getRankUpBundledUriForWeb,
} from '../helpers/celebrationBundledSound.helper';
import {
  getNativePaperTearSource,
  getPaperTearBundledUriForWeb,
} from '../helpers/paperTearBundledSound.helper';
import {
  getNativeSectionCollapseSource,
  getNativeSectionExpandSource,
  getSectionCollapseBundledUriForWeb,
  getSectionExpandBundledUriForWeb,
} from '../helpers/sectionToggleBundledSound.helper';
import {
  getNativeStakeSoundSource,
  getStakeBundledUriForWeb,
} from '../helpers/stakeBundledSound.helper';
import { SOUND_IDS, type SoundId } from '../constants/sounds.constants';

const SOUND_ID_TO_PATH: Record<SoundId, string> = {
  [SOUND_IDS.DAILY_CLAIM]: ASSET_PATHS.SOUNDS.DAILY_CLAIM,
  [SOUND_IDS.CHALLENGE_WIN]: ASSET_PATHS.SOUNDS.CHALLENGE_WIN,
  [SOUND_IDS.CHALLENGE_LOST]: ASSET_PATHS.SOUNDS.CHALLENGE_LOST,
  [SOUND_IDS.STREAK_MILESTONE]: ASSET_PATHS.SOUNDS.STREAK_MILESTONE,
  [SOUND_IDS.UI_SUCCESS]: ASSET_PATHS.SOUNDS.UI_SUCCESS,
  [SOUND_IDS.UI_DANGER_COMMIT]: ASSET_PATHS.SOUNDS.UI_DANGER_COMMIT,
  [SOUND_IDS.UI_ERROR]: ASSET_PATHS.SOUNDS.UI_ERROR,
  [SOUND_IDS.UI_WARNING]: ASSET_PATHS.SOUNDS.UI_WARNING,
  [SOUND_IDS.UI_INFO]: ASSET_PATHS.SOUNDS.UI_INFO,
  [SOUND_IDS.UI_DISMISS]: ASSET_PATHS.SOUNDS.UI_DISMISS,
  [SOUND_IDS.UI_TAP]: ASSET_PATHS.SOUNDS.UI_TAP,
  [SOUND_IDS.UI_TAP_NAV]: ASSET_PATHS.SOUNDS.UI_TAP_NAV,
  [SOUND_IDS.UI_TAP_PICK]: ASSET_PATHS.SOUNDS.UI_TAP_PICK,
  [SOUND_IDS.UI_TAP_STAKE]: ASSET_PATHS.SOUNDS.UI_TAP_STAKE,
  [SOUND_IDS.UI_TAP_SOCIAL]: ASSET_PATHS.SOUNDS.UI_TAP_SOCIAL,
  [SOUND_IDS.UI_SECTION_EXPAND]: ASSET_PATHS.SOUNDS.UI_SECTION_EXPAND,
  [SOUND_IDS.UI_SECTION_COLLAPSE]: ASSET_PATHS.SOUNDS.UI_SECTION_COLLAPSE,
  [SOUND_IDS.UI_MENU_OPEN_SWISH]: ASSET_PATHS.SOUNDS.UI_MENU_OPEN_SWISH,
  [SOUND_IDS.UI_PAPER_TEAR]: ASSET_PATHS.SOUNDS.UI_PAPER_TEAR,
  [SOUND_IDS.LEVEL_UP]: ASSET_PATHS.SOUNDS.LEVEL_UP,
  [SOUND_IDS.RANK_UP]: ASSET_PATHS.SOUNDS.RANK_UP,
};

/** Bundled celebration MP3 web URIs (level-up chime, rank-up forge sequence). */
const CELEBRATION_WEB_URI_GETTERS: Partial<Record<SoundId, () => string | null>> = {
  [SOUND_IDS.LEVEL_UP]: getLevelUpBundledUriForWeb,
  [SOUND_IDS.RANK_UP]: getRankUpBundledUriForWeb,
};


/** Hz + duration (s) + peak gain for short web synth SFX (no CDN required on web). */
const WEB_SYNTH_PRESETS: Record<SoundId, { hz: number; durationSec: number; peakGain: number }> = {
  [SOUND_IDS.UI_TAP]: { hz: 880, durationSec: 0.055, peakGain: 0.16 },
  [SOUND_IDS.UI_TAP_NAV]: { hz: 620, durationSec: 0.06, peakGain: 0.15 },
  [SOUND_IDS.UI_TAP_PICK]: { hz: 1040, durationSec: 0.05, peakGain: 0.15 },
  [SOUND_IDS.UI_TAP_STAKE]: { hz: 420, durationSec: 0.09, peakGain: 0.28 },
  [SOUND_IDS.UI_TAP_SOCIAL]: { hz: 740, durationSec: 0.07, peakGain: 0.15 },
  [SOUND_IDS.UI_SUCCESS]: { hz: 720, durationSec: 0.08, peakGain: 0.1 },
  [SOUND_IDS.UI_DANGER_COMMIT]: { hz: 220, durationSec: 0.12, peakGain: 0.14 },
  [SOUND_IDS.UI_ERROR]: { hz: 180, durationSec: 0.1, peakGain: 0.12 },
  [SOUND_IDS.UI_WARNING]: { hz: 380, durationSec: 0.09, peakGain: 0.11 },
  [SOUND_IDS.UI_INFO]: { hz: 520, durationSec: 0.07, peakGain: 0.09 },
  [SOUND_IDS.UI_DISMISS]: { hz: 420, durationSec: 0.05, peakGain: 0.08 },
  [SOUND_IDS.DAILY_CLAIM]: { hz: 660, durationSec: 0.1, peakGain: 0.12 },
  [SOUND_IDS.STREAK_MILESTONE]: { hz: 990, durationSec: 0.14, peakGain: 0.13 },
  [SOUND_IDS.CHALLENGE_WIN]: { hz: 784, durationSec: 0.16, peakGain: 0.14 },
  [SOUND_IDS.CHALLENGE_LOST]: { hz: 330, durationSec: 0.14, peakGain: 0.12 },
  [SOUND_IDS.UI_SECTION_EXPAND]: { hz: 720, durationSec: 0.06, peakGain: 0.12 },
  [SOUND_IDS.UI_SECTION_COLLAPSE]: { hz: 520, durationSec: 0.055, peakGain: 0.1 },
  [SOUND_IDS.UI_MENU_OPEN_SWISH]: { hz: 900, durationSec: 0.1, peakGain: 0.11 },
  [SOUND_IDS.UI_PAPER_TEAR]: { hz: 240, durationSec: 0.22, peakGain: 0.18 },
  [SOUND_IDS.LEVEL_UP]: { hz: 880, durationSec: 0.3, peakGain: 0.14 },
  [SOUND_IDS.RANK_UP]: { hz: 523.25, durationSec: 0.5, peakGain: 0.12 },
};

class SoundServiceImpl {
  private readonly cache = new Map<SoundId, AudioPlayer>();

  private webAudioCtx: AudioContext | null = null;

  private webUnlockChirpDone = false;

  private stakeWebHtmlAudio: HTMLAudioElement | null = null;

  private stakeWebBundledUriLoaded: string | null = null;

  private stakeWebDecodedBuffer: AudioBuffer | null = null;

  private stakeWebDecodePromise: Promise<AudioBuffer | null> | null = null;

  private sectionExpandWebHtmlAudio: HTMLAudioElement | null = null;

  private sectionCollapseWebHtmlAudio: HTMLAudioElement | null = null;

  private sectionExpandBundledUriLoaded: string | null = null;

  private sectionCollapseBundledUriLoaded: string | null = null;

  private menuOpenSwishWebHtmlAudio: HTMLAudioElement | null = null;

  private menuOpenSwishBundledUriLoaded: string | null = null;

  private paperTearWebHtmlAudio: HTMLAudioElement | null = null;

  private paperTearBundledUriLoaded: string | null = null;

  private readonly celebrationWebHtmlAudio = new Map<SoundId, HTMLAudioElement>();

  private readonly celebrationBundledUriLoaded = new Map<SoundId, string>();

  private audioModeReady = false;

  private getWebSynthPreset(soundId: SoundId): { hz: number; durationSec: number; peakGain: number } {
    const preset = WEB_SYNTH_PRESETS[soundId];
    if (preset) return preset;
    let acc = 0;
    for (let i = 0; i < soundId.length; i += 1) {
      acc = (acc + soundId.charCodeAt(i) * 31) % 280;
    }
    return { hz: 480 + acc, durationSec: 0.08, peakGain: 0.1 };
  }

  /** Create / resume AudioContext (must run from a user input handler on Safari). */
  private ensureWebAudioContextAndResumeSync(): void {
    if (typeof window === 'undefined') return;
    const win = window as typeof window & { webkitAudioContext?: typeof AudioContext };
    const AC = window.AudioContext ?? win.webkitAudioContext;
    if (!AC) return;
    if (!this.webAudioCtx) {
      try {
        this.webAudioCtx = new AC();
      } catch {
        return;
      }
    }
    if (this.webAudioCtx.state === 'suspended') {
      void this.webAudioCtx.resume().catch(() => {
        /* ignore */
      });
    }
  }

  /** One-time ~40ms tone during first gesture — helps macOS Safari treat audio as user-started. */
  private playWebUnlockChirpSync(): void {
    if (this.webUnlockChirpDone || !this.webAudioCtx) return;
    try {
      const ctx = this.webAudioCtx;
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(660, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.04, t0 + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.045);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.05);
      this.webUnlockChirpDone = true;
    } catch {
      /* ignore */
    }
  }

  /** Decode stake MP3 into RAM after gesture (Web Audio — reliable vs HTMLAudio on some browsers). */
  private decodeStakeWebBufferFromGesture(): void {
    if (typeof window === 'undefined' || typeof fetch === 'undefined') return;
    const uri = getStakeBundledUriForWeb();
    if (!uri) return;
    if (this.stakeWebDecodedBuffer || this.stakeWebDecodePromise) return;
    this.stakeWebDecodePromise = (async (): Promise<AudioBuffer | null> => {
      try {
        this.ensureWebAudioContextAndResumeSync();
        const ctx = this.webAudioCtx;
        if (!ctx) return null;
        const res = await fetch(uri, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        const arr = await res.arrayBuffer();
        const buf = await new Promise<AudioBuffer>((resolve, reject) => {
          ctx.decodeAudioData(arr.slice(0), resolve, reject);
        });
        this.stakeWebDecodedBuffer = buf;
        return buf;
      } catch {
        return null;
      } finally {
        this.stakeWebDecodePromise = null;
      }
    })();
  }

  /** Prime bundled menu-open swish MP3 (web HTMLAudio). */
  private primeWebMenuOpenSwishAudio(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    const uri = getMenuOpenSwishBundledUriForWeb();
    if (!uri) return;
    try {
      if (!this.menuOpenSwishWebHtmlAudio) {
        this.menuOpenSwishWebHtmlAudio = new Audio();
      }
      if (this.menuOpenSwishBundledUriLoaded !== uri) {
        this.menuOpenSwishWebHtmlAudio.preload = 'auto';
        this.menuOpenSwishWebHtmlAudio.src = uri;
        this.menuOpenSwishWebHtmlAudio.load();
        this.menuOpenSwishBundledUriLoaded = uri;
      }
    } catch {
      /* ignore */
    }
  }

  /** Prime bundled paper-tear MP3 (web HTMLAudio). */
  private primeWebPaperTearAudio(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    const uri = getPaperTearBundledUriForWeb();
    if (!uri) return;
    try {
      if (!this.paperTearWebHtmlAudio) {
        this.paperTearWebHtmlAudio = new Audio();
      }
      if (this.paperTearBundledUriLoaded !== uri) {
        this.paperTearWebHtmlAudio.preload = 'auto';
        this.paperTearWebHtmlAudio.src = uri;
        this.paperTearWebHtmlAudio.load();
        this.paperTearBundledUriLoaded = uri;
      }
    } catch {
      /* ignore */
    }
  }

  /** Prime bundled section expand/collapse MP3s on first pointer (web HTMLAudio). */
  private primeWebSectionToggleAudio(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    const expandUri = getSectionExpandBundledUriForWeb();
    const collapseUri = getSectionCollapseBundledUriForWeb();
    try {
      if (expandUri) {
        if (!this.sectionExpandWebHtmlAudio) {
          this.sectionExpandWebHtmlAudio = new Audio();
        }
        if (this.sectionExpandBundledUriLoaded !== expandUri) {
          this.sectionExpandWebHtmlAudio.preload = 'auto';
          this.sectionExpandWebHtmlAudio.src = expandUri;
          this.sectionExpandWebHtmlAudio.load();
          this.sectionExpandBundledUriLoaded = expandUri;
        }
      }
      if (collapseUri) {
        if (!this.sectionCollapseWebHtmlAudio) {
          this.sectionCollapseWebHtmlAudio = new Audio();
        }
        if (this.sectionCollapseBundledUriLoaded !== collapseUri) {
          this.sectionCollapseWebHtmlAudio.preload = 'auto';
          this.sectionCollapseWebHtmlAudio.src = collapseUri;
          this.sectionCollapseWebHtmlAudio.load();
          this.sectionCollapseBundledUriLoaded = collapseUri;
        }
      }
    } catch {
      /* ignore */
    }
  }

  /** Prime bundled stake MP3 on first pointer (helps first tap `play()` succeed on Safari). */
  private primeWebStakeCoinAudio(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    const uri = getStakeBundledUriForWeb();
    if (!uri) return;
    try {
      if (!this.stakeWebHtmlAudio) {
        this.stakeWebHtmlAudio = new Audio();
      }
      if (this.stakeWebBundledUriLoaded !== uri) {
        this.stakeWebHtmlAudio.preload = 'auto';
        this.stakeWebHtmlAudio.src = uri;
        this.stakeWebHtmlAudio.load();
        this.stakeWebBundledUriLoaded = uri;
      }
    } catch {
      /* ignore */
    }
  }

  private playWebStakeCoinMp3(): void {
    if (typeof window === 'undefined') {
      this.playWebSynthClip(SOUND_IDS.UI_TAP_STAKE);
      return;
    }
    const buf = this.stakeWebDecodedBuffer;
    if (buf && this.webAudioCtx) {
      try {
        this.ensureWebAudioContextAndResumeSync();
        const ctx = this.webAudioCtx;
        if (!ctx) {
          this.playWebStakeCoinHtml5OrSynth();
          return;
        }
        const src = ctx.createBufferSource();
        const g = ctx.createGain();
        g.gain.setValueAtTime(1, ctx.currentTime);
        src.buffer = buf;
        src.connect(g);
        g.connect(ctx.destination);
        src.start(ctx.currentTime);
        return;
      } catch (e: unknown) {
        logger.warn(
          '[SoundService] web stake buffer play failed, trying HTMLAudio',
          e instanceof Error ? e.message : String(e)
        );
      }
    }
    this.playWebStakeCoinHtml5OrSynth();
  }

  private playWebStakeCoinHtml5OrSynth(): void {
    if (typeof Audio === 'undefined') {
      this.playWebSynthClip(SOUND_IDS.UI_TAP_STAKE);
      return;
    }
    const uri = getStakeBundledUriForWeb();
    if (!uri) {
      this.playWebSynthClip(SOUND_IDS.UI_TAP_STAKE);
      return;
    }
    try {
      if (!this.stakeWebHtmlAudio) {
        this.stakeWebHtmlAudio = new Audio();
      }
      if (this.stakeWebBundledUriLoaded !== uri) {
        this.stakeWebHtmlAudio.preload = 'auto';
        this.stakeWebHtmlAudio.src = uri;
        this.stakeWebHtmlAudio.load();
        this.stakeWebBundledUriLoaded = uri;
      }
      this.stakeWebHtmlAudio.volume = 1;
      this.stakeWebHtmlAudio.currentTime = 0;
      void this.stakeWebHtmlAudio.play().catch((e: unknown) => {
        logger.warn(
          '[SoundService] web stake HTMLAudio play failed, using synth',
          e instanceof Error ? e.message : String(e)
        );
        this.playWebSynthClip(SOUND_IDS.UI_TAP_STAKE);
      });
    } catch (e: unknown) {
      logger.warn(
        '[SoundService] web stake mp3 failed',
        e instanceof Error ? e.message : String(e)
      );
      this.playWebSynthClip(SOUND_IDS.UI_TAP_STAKE);
    }
  }

  /** Web: play bundled menu-open swish MP3 (HTMLAudio; synth fallback). */
  private playWebMenuOpenSwishMp3(): void {
    const soundId = SOUND_IDS.UI_MENU_OPEN_SWISH;
    if (typeof window === 'undefined' || typeof Audio === 'undefined') {
      this.playWebSynthClip(soundId);
      return;
    }
    const uri = getMenuOpenSwishBundledUriForWeb();
    if (!uri) {
      this.playWebSynthClip(soundId);
      return;
    }
    try {
      if (!this.menuOpenSwishWebHtmlAudio) {
        this.menuOpenSwishWebHtmlAudio = new Audio();
      }
      if (this.menuOpenSwishBundledUriLoaded !== uri) {
        this.menuOpenSwishWebHtmlAudio.preload = 'auto';
        this.menuOpenSwishWebHtmlAudio.src = uri;
        this.menuOpenSwishWebHtmlAudio.load();
        this.menuOpenSwishBundledUriLoaded = uri;
      }
      this.menuOpenSwishWebHtmlAudio.volume = 1;
      this.menuOpenSwishWebHtmlAudio.currentTime = 0;
      void this.menuOpenSwishWebHtmlAudio.play().catch((e: unknown) => {
        logger.warn(
          '[SoundService] web menu swish HTMLAudio play failed, using synth',
          e instanceof Error ? e.message : String(e)
        );
        this.playWebSynthClip(soundId);
      });
    } catch (e: unknown) {
      logger.warn(
        '[SoundService] web menu swish mp3 failed',
        e instanceof Error ? e.message : String(e)
      );
      this.playWebSynthClip(soundId);
    }
  }

  /** Web: play bundled paper-tear MP3 (HTMLAudio; synth fallback). */
  private playWebPaperTearMp3(): void {
    const soundId = SOUND_IDS.UI_PAPER_TEAR;
    if (typeof window === 'undefined' || typeof Audio === 'undefined') {
      this.playWebSynthClip(soundId);
      return;
    }
    const uri = getPaperTearBundledUriForWeb();
    if (!uri) {
      this.playWebSynthClip(soundId);
      return;
    }
    try {
      if (!this.paperTearWebHtmlAudio) {
        this.paperTearWebHtmlAudio = new Audio();
      }
      if (this.paperTearBundledUriLoaded !== uri) {
        this.paperTearWebHtmlAudio.preload = 'auto';
        this.paperTearWebHtmlAudio.src = uri;
        this.paperTearWebHtmlAudio.load();
        this.paperTearBundledUriLoaded = uri;
      }
      this.paperTearWebHtmlAudio.volume = 1;
      this.paperTearWebHtmlAudio.currentTime = 0;
      void this.paperTearWebHtmlAudio.play().catch((e: unknown) => {
        logger.warn(
          '[SoundService] web paper tear HTMLAudio play failed, using synth',
          e instanceof Error ? e.message : String(e)
        );
        this.playWebSynthClip(soundId);
      });
    } catch (e: unknown) {
      logger.warn(
        '[SoundService] web paper tear mp3 failed',
        e instanceof Error ? e.message : String(e)
      );
      this.playWebSynthClip(soundId);
    }
  }

  /** Web: play bundled section expand/collapse MP3 (HTMLAudio; synth fallback if URI/play fails). */
  private playWebSectionBundledMp3(soundId: SoundId): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') {
      this.playWebSynthClip(soundId);
      return;
    }
    const isExpand = soundId === SOUND_IDS.UI_SECTION_EXPAND;
    const uri = isExpand ? getSectionExpandBundledUriForWeb() : getSectionCollapseBundledUriForWeb();
    const audio = isExpand ? this.sectionExpandWebHtmlAudio : this.sectionCollapseWebHtmlAudio;
    if (!uri) {
      this.playWebSynthClip(soundId);
      return;
    }
    try {
      let el = audio;
      if (!el) {
        el = new Audio();
        if (isExpand) {
          this.sectionExpandWebHtmlAudio = el;
        } else {
          this.sectionCollapseWebHtmlAudio = el;
        }
      }
      const prevLoaded = isExpand ? this.sectionExpandBundledUriLoaded : this.sectionCollapseBundledUriLoaded;
      if (prevLoaded !== uri) {
        el.preload = 'auto';
        el.src = uri;
        el.load();
        if (isExpand) {
          this.sectionExpandBundledUriLoaded = uri;
        } else {
          this.sectionCollapseBundledUriLoaded = uri;
        }
      }
      el.volume = 1;
      el.currentTime = 0;
      void el.play().catch((e: unknown) => {
        logger.warn(
          '[SoundService] web section toggle HTMLAudio play failed, using synth',
          e instanceof Error ? e.message : String(e)
        );
        this.playWebSynthClip(soundId);
      });
    } catch (e: unknown) {
      logger.warn(
        '[SoundService] web section toggle mp3 failed',
        e instanceof Error ? e.message : String(e)
      );
      this.playWebSynthClip(soundId);
    }
  }

  /** Prime bundled celebration MP3s (level-up / rank-up) on first pointer (web HTMLAudio). */
  private primeWebCelebrationAudio(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    for (const [soundId, getUri] of Object.entries(CELEBRATION_WEB_URI_GETTERS)) {
      const uri = getUri();
      if (!uri) continue;
      try {
        let el = this.celebrationWebHtmlAudio.get(soundId as SoundId);
        if (!el) {
          el = new Audio();
          this.celebrationWebHtmlAudio.set(soundId as SoundId, el);
        }
        if (this.celebrationBundledUriLoaded.get(soundId as SoundId) !== uri) {
          el.preload = 'auto';
          el.src = uri;
          el.load();
          this.celebrationBundledUriLoaded.set(soundId as SoundId, uri);
        }
      } catch {
        /* ignore */
      }
    }
  }

  /** Web: play bundled celebration MP3 (HTMLAudio; synth fallback). */
  private playWebCelebrationMp3(soundId: SoundId): void {
    const getUri = CELEBRATION_WEB_URI_GETTERS[soundId];
    const uri = getUri ? getUri() : null;
    if (typeof window === 'undefined' || typeof Audio === 'undefined' || !uri) {
      this.playWebSynthClip(soundId);
      return;
    }
    try {
      let el = this.celebrationWebHtmlAudio.get(soundId);
      if (!el) {
        el = new Audio();
        this.celebrationWebHtmlAudio.set(soundId, el);
      }
      if (this.celebrationBundledUriLoaded.get(soundId) !== uri) {
        el.preload = 'auto';
        el.src = uri;
        el.load();
        this.celebrationBundledUriLoaded.set(soundId, uri);
      }
      el.volume = 1;
      el.currentTime = 0;
      void el.play().catch((e: unknown) => {
        logger.warn(
          '[SoundService] web celebration HTMLAudio play failed, using synth',
          e instanceof Error ? e.message : String(e)
        );
        this.playWebSynthClip(soundId);
      });
    } catch (e: unknown) {
      logger.warn(
        '[SoundService] web celebration mp3 failed',
        e instanceof Error ? e.message : String(e)
      );
      this.playWebSynthClip(soundId);
    }
  }

  private playWebSynthClip(soundId: SoundId): void {
    this.ensureWebAudioContextAndResumeSync();
    const ctx = this.webAudioCtx;
    if (!ctx) return;
    try {
      const { hz, durationSec, peakGain } = this.getWebSynthPreset(soundId);
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(hz, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(Math.min(0.22, peakGain), t0 + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + durationSec);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + durationSec + 0.02);
    } catch (e: unknown) {
      logger.warn(
        '[SoundService] web synth play failed',
        e instanceof Error ? e.message : String(e)
      );
    }
  }

  private async ensureAudioMode(): Promise<void> {
    if (this.audioModeReady) return;
    try {
      await setAudioModeAsync({
        playsInSilentMode: true,
        allowsRecording: false,
        shouldPlayInBackground: false,
        shouldRouteThroughEarpiece: false,
        interruptionMode: 'duckOthers',
      });
      this.audioModeReady = true;
    } catch (err: unknown) {
      if (Platform.OS === 'web') {
        this.audioModeReady = true;
        return;
      }
      logger.warn('[SoundService] setAudioModeAsync failed', err instanceof Error ? err.message : String(err));
    }
  }

  /** Synchronously create/resume AudioContext and play unlock chirp — call within a user gesture handler. */
  warmWebAudioContextSync(): void {
    if (Platform.OS !== 'web') return;
    this.ensureWebAudioContextAndResumeSync();
    this.playWebUnlockChirpSync();
  }

  /** Prime audio (web: AudioContext + optional unlock chirp; native: expo session). */
  async warmFromUserGesture(): Promise<void> {
    if (Platform.OS === 'web') {
      this.warmWebAudioContextSync();
      this.primeWebStakeCoinAudio();
      this.primeWebSectionToggleAudio();
      this.primeWebMenuOpenSwishAudio();
      this.primeWebPaperTearAudio();
      this.primeWebCelebrationAudio();
      this.decodeStakeWebBufferFromGesture();
      return;
    }
    await this.ensureAudioMode();
  }

  async play(soundId: SoundId): Promise<void> {
    const path = SOUND_ID_TO_PATH[soundId];

    if (Platform.OS === 'web') {
      this.ensureWebAudioContextAndResumeSync();
      if (soundId === SOUND_IDS.UI_TAP_STAKE) {
        this.playWebStakeCoinMp3();
        return;
      }
      if (soundId === SOUND_IDS.UI_SECTION_EXPAND || soundId === SOUND_IDS.UI_SECTION_COLLAPSE) {
        this.playWebSectionBundledMp3(soundId);
        return;
      }
      if (soundId === SOUND_IDS.UI_MENU_OPEN_SWISH) {
        this.playWebMenuOpenSwishMp3();
        return;
      }
      if (soundId === SOUND_IDS.UI_PAPER_TEAR) {
        this.playWebPaperTearMp3();
        return;
      }
      if (soundId === SOUND_IDS.LEVEL_UP || soundId === SOUND_IDS.RANK_UP) {
        this.playWebCelebrationMp3(soundId);
        return;
      }
      this.playWebSynthClip(soundId);
      return;
    }

    await this.ensureAudioMode();

    const bundledStake =
      soundId === SOUND_IDS.UI_TAP_STAKE ? getNativeStakeSoundSource() : null;
    const bundledExpand =
      soundId === SOUND_IDS.UI_SECTION_EXPAND ? getNativeSectionExpandSource() : null;
    const bundledCollapse =
      soundId === SOUND_IDS.UI_SECTION_COLLAPSE ? getNativeSectionCollapseSource() : null;
    const bundledMenuSwish =
      soundId === SOUND_IDS.UI_MENU_OPEN_SWISH ? getNativeMenuOpenSwishSource() : null;
    const bundledPaperTear =
      soundId === SOUND_IDS.UI_PAPER_TEAR ? getNativePaperTearSource() : null;
    const bundledLevelUp =
      soundId === SOUND_IDS.LEVEL_UP ? getNativeLevelUpSource() : null;
    const bundledRankUp =
      soundId === SOUND_IDS.RANK_UP ? getNativeRankUpSource() : null;
    const bundled =
      bundledStake ??
      bundledExpand ??
      bundledCollapse ??
      bundledMenuSwish ??
      bundledPaperTear ??
      bundledLevelUp ??
      bundledRankUp;
    const source: number | { uri: string } =
      bundled != null ? bundled : { uri: getAssetUrl(path) };

    try {
      let sound = this.cache.get(soundId);
      if (!sound) {
        sound = createAudioPlayer(source);
        sound.volume = 1;
        sound.muted = false;
        this.cache.set(soundId, sound);
      }
      // seek to 0 only if loaded — on the very first play the player is still loading and seekTo would no-op or throw.
      // Calling play() on a not-yet-loaded player queues the playback to start once load completes.
      if (sound.isLoaded) {
        await sound.seekTo(0);
      }
      sound.play();
    } catch (err: unknown) {
      logger.warn(
        `[SoundService] play failed for ${soundId} (upload mp3 to R2: ${path})`,
        err instanceof Error ? err.message : String(err)
      );
    }
  }

  async stopAll(): Promise<void> {
    if (Platform.OS === 'web') {
      return;
    }

    for (const sound of this.cache.values()) {
      try {
        if (sound.isLoaded) {
          sound.pause();
          await sound.seekTo(0);
        }
      } catch {
        /* ignore */
      }
    }
  }

  async unloadAll(): Promise<void> {
    if (Platform.OS === 'web') {
      try {
        if (this.stakeWebHtmlAudio) {
          this.stakeWebHtmlAudio.pause();
          this.stakeWebHtmlAudio.removeAttribute('src');
        }
      } catch {
        /* ignore */
      }
      this.stakeWebHtmlAudio = null;
      this.stakeWebBundledUriLoaded = null;
      this.stakeWebDecodedBuffer = null;
      this.stakeWebDecodePromise = null;
      try {
        if (this.sectionExpandWebHtmlAudio) {
          this.sectionExpandWebHtmlAudio.pause();
          this.sectionExpandWebHtmlAudio.removeAttribute('src');
        }
        if (this.sectionCollapseWebHtmlAudio) {
          this.sectionCollapseWebHtmlAudio.pause();
          this.sectionCollapseWebHtmlAudio.removeAttribute('src');
        }
      } catch {
        /* ignore */
      }
      this.sectionExpandWebHtmlAudio = null;
      this.sectionCollapseWebHtmlAudio = null;
      this.sectionExpandBundledUriLoaded = null;
      this.sectionCollapseBundledUriLoaded = null;
      try {
        if (this.menuOpenSwishWebHtmlAudio) {
          this.menuOpenSwishWebHtmlAudio.pause();
          this.menuOpenSwishWebHtmlAudio.removeAttribute('src');
        }
      } catch {
        /* ignore */
      }
      this.menuOpenSwishWebHtmlAudio = null;
      this.menuOpenSwishBundledUriLoaded = null;
      try {
        if (this.paperTearWebHtmlAudio) {
          this.paperTearWebHtmlAudio.pause();
          this.paperTearWebHtmlAudio.removeAttribute('src');
        }
      } catch {
        /* ignore */
      }
      this.paperTearWebHtmlAudio = null;
      this.paperTearBundledUriLoaded = null;
      for (const el of this.celebrationWebHtmlAudio.values()) {
        try {
          el.pause();
          el.removeAttribute('src');
        } catch {
          /* ignore */
        }
      }
      this.celebrationWebHtmlAudio.clear();
      this.celebrationBundledUriLoaded.clear();
      try {
        await this.webAudioCtx?.close();
      } catch {
        /* ignore */
      }
      this.webAudioCtx = null;
      this.webUnlockChirpDone = false;
      this.audioModeReady = false;
      return;
    }

    for (const [id, sound] of this.cache.entries()) {
      try {
        sound.remove();
      } catch {
        /* ignore */
      }
      this.cache.delete(id);
    }
    this.audioModeReady = false;
  }
}

export const soundService = new SoundServiceImpl();
